import { FC } from 'react';
import { AutoComplete, Checkbox } from 'antd';
import {
  daysOfWeek,
  ORSchedulerPropsType,
  TimeSlotControlsProps,
  WeeklySchedule,
} from './ScheduleTypes';
import { generateTimeSlots } from './helpers';
import { ACTION_TYPES, defaultWeeklySchedule } from './scheduleState/ScheduleReducer';
import { useSchedule } from './scheduleState/ScheduleContext';
import { validateTimeFormat } from './helpers';
import { ReactComponent as CalendarIcon } from '../../assets/schedule-calendar-icon.svg';

const TimeSlotControls: FC<TimeSlotControlsProps> = ({ OR, periodId }) => {
  const { hospitalSchedule, hospitalDispatch } = useSchedule();
  const orSchedule =
    (hospitalSchedule.find((period) => period.id === periodId)?.schedule[
      OR || 'AllORs'
    ] as WeeklySchedule) || defaultWeeklySchedule;

  const applyToAllDays = orSchedule?.All?.checked;
  return (
    <>
      <div className="w-full grid grid-cols-[16%_42%_42%] text-gray-500 mt-5 -mb-2">
        <div></div>
        <div>Start Time</div>
        <div>End Time</div>
      </div>
      <div className="w-full">
        {daysOfWeek
          .filter((d) => (applyToAllDays ? d === 'All' : d !== 'All'))
          .map((day, idx) => {
            return (
              <div className="w-full grid grid-cols-[16%_42%_42%]" key={idx}>
                <div className="flex justify-start items-center h-14">
                  {day !== 'All' && (
                    <Checkbox
                      checked={orSchedule[day]?.checked || false}
                      onChange={(e) => {
                        hospitalDispatch({
                          type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
                          payload: {
                            periodId,
                            OR,
                            day,
                            updatedValue: {
                              ...orSchedule[day],
                              checked: e.target.checked,
                            },
                          },
                        });
                      }}
                    >
                      {day}:
                    </Checkbox>
                  )}
                  {day == 'All' && <span className="font-bold">All:</span>}
                </div>
                <div className="h-20">
                  <AutoComplete
                    options={generateTimeSlots()}
                    value={orSchedule?.[day]?.startTime}
                    className="w-11/12"
                    disabled={!orSchedule?.[day]?.checked}
                    onChange={(value) => {
                      const updatedValue = value;
                      hospitalDispatch({
                        type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
                        payload: {
                          periodId,
                          OR,
                          day,
                          updatedValue: {
                            ...orSchedule[day],
                            startTime: updatedValue,
                          },
                        },
                      });
                    }}
                    onBlur={(e) => {
                      if (!validateTimeFormat((e.target as HTMLInputElement).value)) {
                        hospitalDispatch({
                          type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
                          payload: {
                            periodId,
                            OR,
                            day,
                            updatedValue: {
                              ...orSchedule[day],
                              startTime: '08:00',
                            },
                          },
                        });
                      }
                    }}
                  />
                </div>
                <div className="h-20 -mb-10 ">
                  <AutoComplete
                    options={generateTimeSlots()}
                    value={orSchedule[day].endTime}
                    className="w-11/12"
                    disabled={!orSchedule[day]?.checked}
                    onChange={(value) => {
                      const updatedValue = value;
                      hospitalDispatch({
                        type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
                        payload: {
                          periodId,
                          OR,
                          day,
                          updatedValue: {
                            ...orSchedule[day],
                            endTime: updatedValue,
                          },
                        },
                      });
                    }}
                    onBlur={(e) => {
                      if (!validateTimeFormat((e.target as HTMLInputElement).value)) {
                        hospitalDispatch({
                          type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
                          payload: {
                            periodId,
                            OR,
                            day,
                            updatedValue: {
                              ...orSchedule[day],
                              endTime: '16:00',
                            },
                          },
                        });
                      }
                    }}
                  />
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

const ORScheduler: FC<ORSchedulerPropsType> = ({ periodId, OR, schedule, hospitalDispatch }) => {
  const handleApplyToAllDays = (applyToAllDays: boolean) => {
    hospitalDispatch({
      type: ACTION_TYPES.UPDATE_DAY_SCHEDULE,
      payload: {
        periodId,
        OR,
        day: 'All',
        updatedValue: {
          ...schedule.All,
          checked: applyToAllDays,
        },
      },
    });
  };
  const applyToAllDays = schedule?.All?.checked;

  return (
    <>
      <div>
        <Checkbox checked={applyToAllDays} onChange={(e) => handleApplyToAllDays(e.target.checked)}>
          <span className="font-bold mr-2">Apply to all days of the week</span>
          <span>
            <CalendarIcon className="w-9 h-9 inline-block ml-2" />
          </span>
        </Checkbox>
      </div>
      <div className="w-full space-y-4">
        {schedule?.All?.checked ? (
          <TimeSlotControls OR={OR} periodId={periodId} />
        ) : (
          <TimeSlotControls OR={OR} periodId={periodId} />
        )}
      </div>
    </>
  );
};

export default ORScheduler;
